import { FC, useState } from 'react';
import { observer } from 'mobx-react';
import { AppIcon, AppText, Flex, Select, styled, Theme, Tooltip } from '@streem/ui-react';

import { statusOptions, StatusOptionValue, StatusOptionValueType } from './status_option_value';

import { StreemApiUserAvailabilityAvailabilityStatus } from '@streem/api';

type AvailabilityIndicatorProps = {
    selectedAvailabilityStatus: StatusOptionValueType;
    setSelectedStatus: (selectedOption: StatusOptionValueType) => void;
    onSelectionCallback: (
        selectedOption: StatusOptionValueType,
    ) => Promise<StreemApiUserAvailabilityAvailabilityStatus>;
    isUpdatingSelection: boolean;
    isReserved: boolean;
    isInCall: boolean;
};

export const AvailabilityIndicator: FC<AvailabilityIndicatorProps> = observer(
    ({
        isUpdatingSelection: isUpdating,
        isReserved: reserved,
        isInCall: inCall,
        selectedAvailabilityStatus: selectedOption,
        setSelectedStatus,
        onSelectionCallback,
    }) => {
        const [showTooltip, setShowTooltip] = useState(false);

        // ensures we don't have a desync of selected option and state when presenting the UI
        // Group Reservation and Expert Availability still require normalization in the client
        // see "use_expert_availability" and "use_group_reservation" for the sources of data
        // "isReserved" and "isInCall" come from Group Reservation
        // "selectedOption" comes from User Availability - NOTE: this could be the status directly vs the selected option
        // TODO: move this to "Group Reservation" as there is already AvailabilityStatus normalizing there.
        // ticket here: https://ftdr.atlassian.net/browse/ST-8044
        const isReserved = selectedOption.label === 'Reserved' && reserved;
        const isInCall = selectedOption.label === 'In Call' && inCall;

        const isDisabledStatus = isReserved || isInCall || isUpdating;

        const getToolTipMessage = () => {
            if (isReserved) return 'reserved';
            if (isInCall) return 'in a call';
        };

        return (
            <>
                <Tooltip
                    message={
                        <AppText size="small" style={{ whiteSpace: 'nowrap' }}>
                            Cannot set status while {getToolTipMessage()}
                        </AppText>
                    }
                    offsetValue={-13}
                    placement={'bottom'}
                    showTooltip={showTooltip}
                >
                    <AvailabilityWrapper
                        data-testid="availability-container"
                        onMouseEnter={() => {
                            if (isReserved || isInCall) {
                                setShowTooltip(true);
                            }
                        }}
                        onMouseLeave={() => setShowTooltip(false)}
                    >
                        <Select<StatusOptionValue>
                            setExternallyControlledOption={setSelectedStatus}
                            externallyControlledOption={selectedOption}
                            options={statusOptions}
                            onSelect={onSelectionCallback}
                            initialValue={selectedOption}
                            isDisabled={isDisabledStatus}
                            isSearchable={false}
                            filterOption={option => {
                                const { data } = option as { data: StatusOptionValueType };
                                return data.label !== 'Reserved' && data.label !== 'In Call';
                            }}
                            components={{
                                ClearIndicator: () => null,
                                IndicatorSeparator: () => null,
                                Input: () => null,
                                SingleValue: ({ data }: { data: StatusOptionValueType }) => {
                                    return (
                                        <SelectedStatusContainer data-testid="selected-status">
                                            {data.value.icon}
                                            <SelectedStatusLabel
                                                headingFontFamily
                                                data-testid={`selected-status-label-${data.label}`}
                                                style={{
                                                    color: isDisabledStatus
                                                        ? Theme.colors.grey60
                                                        : undefined,
                                                }}
                                            >
                                                {data.label}
                                            </SelectedStatusLabel>
                                        </SelectedStatusContainer>
                                    );
                                },
                            }}
                            formatOptionLabel={option => {
                                const { value } = option as {
                                    value: StatusOptionValue;
                                };
                                return (
                                    <OptionLabel value={value} selectedOption={selectedOption} />
                                );
                            }}
                            styles={{
                                control: (base: any, { selectProps }) => ({
                                    ...base,
                                    width: '170px',
                                    height: '40px',
                                    borderRadius: '32px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    ':hover': {
                                        background: Theme.colors.blue05,
                                    },
                                    background: selectProps.isDisabled
                                        ? Theme.colors.grey10
                                        : undefined,
                                }),
                                menu: (base: any) => ({
                                    ...base,
                                    marginTop: '6px',
                                    borderRadius: 4,
                                    right: 0,
                                    width: '242px',
                                }),
                                menuList: (base: any) => ({
                                    ...base,
                                    padding: 0,
                                    width: '242px',
                                }),
                                option: (base: any, { isFocused }) => ({
                                    ...base,
                                    borderRadius: '4px',
                                    backgroundColor: isFocused ? Theme.colors.blue05 : undefined,
                                    cursor: 'pointer',
                                    ':active': {
                                        backgroundColor: Theme.colors.blue05,
                                    },
                                }),
                                valueContainer: () => ({
                                    height: '100%',
                                }),
                                dropdownIndicator: (base: any, { selectProps }) => ({
                                    ...base,
                                    transform: selectProps.menuIsOpen ? 'rotate(180deg)' : null,
                                    transition: 'all .2s ease',
                                    color: selectProps.isDisabled
                                        ? Theme.colors.grey10
                                        : Theme.colors.grey50,
                                    ':hover': {
                                        color: Theme.colors.grey50,
                                    },
                                }),
                            }}
                        />
                    </AvailabilityWrapper>
                </Tooltip>
            </>
        );
    },
);

AvailabilityIndicator.displayName = 'AvailabilityIndicator';

const AvailabilityWrapper = styled.div({
    display: 'flex',
    marginRight: '24px',
});

const StatusCircle = styled.div<{ label: string }>(({ label, theme }) => ({
    height: '10px',
    width: '10px',
    borderRadius: 50,
    backgroundColor: label === 'Ready' ? theme.colors.green60 : theme.colors.red60,
    marginRight: theme.spacing.s,
}));

const SelectedStatusContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    marginLeft: '12px',
});

const SelectedStatusLabel = styled(AppText)({
    width: '88px',
    display: 'block',
    textAlign: 'center',
});

const OptionContainer = styled(Flex)(({ theme }) => ({
    alignItems: 'baseline',
    padding: theme.spacing.xs,
}));

const OptionLabel: FC<{
    value: StatusOptionValue;
    selectedOption: StatusOptionValueType;
}> = ({ value, selectedOption }) => (
    <OptionContainer
        data-testid={`availability-${value.label.toLowerCase().replace(/\s+/g, '-')}-button`}
    >
        <StatusCircle label={value.label} />
        <div>
            <AppText style={{ display: 'block', marginBottom: '4px' }}>{value.label}</AppText>
            <AppText size="small">{value.description}</AppText>
        </div>
        {selectedOption.label === value.label && (
            <AppIcon
                name="DoneIcon"
                color={Theme.colors.blue40}
                style={{ alignSelf: 'center', marginLeft: '18px', flexGrow: 1 }}
            />
        )}
    </OptionContainer>
);
